import React from 'react';
import moment from 'moment';
import {ButtonInfo} from 'components/buttons';
import SelfDeliveryInvoiceActions from 'routes/App/reports/ReportsTable/SelfDeliveryInvoiceActions';
import roundTheNumber from 'helpers/functionUtils/roundTheNumber';
import {ORDER_TYPES} from 'helpers/constants/orders';
import {feedbackTypes} from 'helpers/constants/feedback';

const OrderType = [
  'PickUp',
  'Delivery',
  'SauceDispatch',
  'DineIn',
  'UberEatsDelivery',
  'UberPickUp'
];

export const REPORT_TYPES = {
  SALES_SUMMARY: 0,
  CUSTOMERS_SUMMARY: 1,
  PAYOUT_SUMMARY: 2,
  SELF_DELIVERY_SUMMARY: 3,
  REFUNDS_SUMMARY: 5,
  FEEDBACK_SUMMARY: 6,
  SPECIAL_ORDERS: 7,
  BOOK_A_DRIVER_SUMMARY: 8,
  DISCOUNT_SUMMARY: 9,
  CASHBACK_USAGE: 10,
  SALES_BREAKDOWN_SUMMARY: 11,
  SELF_DELIVERY_INVOICES: 12
};

export const REPORT_DATEPICKER_INDEX = {
  ALL_PERIOD: 0,
  TODAY: 1,
  THIS_WEEK: 2,
  LAST_WEEK: 3,
  THIS_MONTH: 4,
  LAST_MONTH: 5,
  CUSTOM_RANGE: 6,
  NEXT_WEEK: 7,
  NEXT_MONTH: 8
};

export const REPORT_TIME_PERIOD_TITLES = {
  0: 'all_period',
  1: 'today',
  2: 'this_week',
  3: 'last_week',
  4: 'this_month',
  5: 'last_month',
  6: 'custom_range',
  7: 'next_week',
  8: 'next_month'
};

export const REPORT_TYPE_TITLES = {
  0: 'sales_summary',
  1: 'customers_summary',
  2: 'payout_summary',
  3: 'self_delivery_summary',
  5: 'refunds_summary',
  6: 'feedback_summary',
  7: 'special_orders',
  8: 'book_a_driver_summary',
  9: 'discount_summary',
  10: 'cashback_usage',
  11: 'sales_breakdown_summary',
  12: 'self_delivery_invoices'
};

// Which date the Self Delivery Summary period is applied to (BFF enum values)
export const SELF_DELIVERY_DATE_FILTER = {
  ORDER_DATE: 'OrderDate',
  CHARGE_DATE: 'ChargeDate'
};

export const SELF_DELIVERY_DATE_FILTER_OPTIONS = [
  {value: SELF_DELIVERY_DATE_FILTER.ORDER_DATE, label: 'Order date'},
  {value: SELF_DELIVERY_DATE_FILTER.CHARGE_DATE, label: 'Charge date'}
];

export const SELF_DELIVERY_DATE_FILTER_TITLES = {
  [SELF_DELIVERY_DATE_FILTER.ORDER_DATE]: 'order_date',
  [SELF_DELIVERY_DATE_FILTER.CHARGE_DATE]: 'charge_date'
};

export const POSTHOG_EVENT_NAMES = {
  REPORTS_EXPORT_CLICKED: 'reports_export_clicked',
  REPORTS_BULK_EXPORT_CLICKED: 'reports_bulk_export_clicked',
  REPORTS_BULK_EXPORT_SENT: 'reports_bulk_export_sent',
  REPORTS_GENERATED: 'reports_generated',
  REPORTS_GENERATE_CLICKED: 'reports_generate_clicked',
  SELF_DELIVERY_INVOICE_VIEW_CLICKED: 'self_delivery_invoice_view_clicked',
  SELF_DELIVERY_INVOICE_ORDER_BREAKDOWN_CLICKED:
    'self_delivery_invoice_order_breakdown_clicked'
};

export const SALES_BREAKDOWN_CHANNEL_OPTIONS = [
  {label: 'Uber Eats', value: 'uber'},
  {label: 'DoorDash', value: 'doordash'},
  {label: 'Grubhub', value: 'grubhub'},
  {label: 'Slice', value: 'slice'},
  {label: 'Sauce Storefront', value: 'sauce'}
];

export const SALES_BREAKDOWN_FULFILLMENT_OPTIONS = [
  {label: 'Pickup', value: 'pickup'},
  {label: 'Sauce Delivery', value: 'sauce_delivery'},
  {label: '3rd Party Delivery', value: 'third_party_delivery'},
  {label: 'Location Delivery', value: 'location_delivery'}
];

const renderDate = (whatRender, whatRenderIfNoData = '') => {
  return whatRender ? moment(whatRender).format('LL') : whatRenderIfNoData;
};

const renderDateUtc = (whatRender, whatRenderIfNoData = '') => {
  return whatRender
    ? moment(whatRender).utc().format('LL')
    : whatRenderIfNoData;
};

const renderDateOnly = (whatRender, whatRenderIfNoData = '-') => {
  if (!whatRender) {
    return whatRenderIfNoData;
  }
  const match = String(whatRender).match(/^(\d{4}-\d{2}-\d{2})/);
  const parsed = match ? moment.utc(match[1]) : moment.utc(whatRender);
  return parsed.isValid() ? parsed.format('LL') : whatRenderIfNoData;
};

const renderCount = (value) => (value == null ? '-' : value);

const renderAmounts = (
  whatRender,
  whatRenderIfNoData = '$0.00',
  isNegative = false
) => {
  if (whatRender) {
    let strNumber = roundTheNumber(whatRender);
    if (strNumber.startsWith('-')) {
      strNumber =
        strNumber.length > 7
          ? [strNumber.slice(0, -6), ',', strNumber.slice(-6)]
              .join('')
              .replace('-', '-$')
          : strNumber.replace('-', '-$');
      return strNumber;
    }
    strNumber =
      strNumber.length > 6
        ? [strNumber.slice(0, -6), ',', strNumber.slice(-6)].join('')
        : strNumber;
    return isNegative ? `-$${strNumber}` : `$${strNumber}`;
  }
  return whatRenderIfNoData;
};

const outOfRangePayoutsText = (rowData, whatRenderIfNoData = '') => {
  if (
    rowData.coveredDatesByPayouts.length &&
    rowData.coveredDatesByPayouts.length > 0
  ) {
    const outOfRangeDatesArr = rowData.coveredDatesByPayouts.filter(
      (date) => date !== rowData.salesDate
    );

    if (outOfRangeDatesArr.length > 0) {
      const showDatesArr = rowData.coveredDatesByPayouts
        .sort()
        .map((date) => moment(date).format('MMM D'));

      return (
        <span className='reportsGrayText'>
          Orders from {showDatesArr[0]}-{showDatesArr[showDatesArr.length - 1]}
        </span>
      );
    }
    return whatRenderIfNoData;
  }
  return whatRenderIfNoData;
};

export const FIELDS_CUSTOMERS = [
  {
    title: 'Last Order',
    renderer: (cellText) => (
      <div className='cellNoWrap'>
        {renderDate(cellText.lastOrderCreationDate, '-')}
      </div>
    )
  },
  {
    title: 'Name',
    renderer: (cellText) => <div>{cellText.name || ''}</div>
  },
  {
    title: 'Email',
    renderer: (cellText) => <div>{cellText.email || ''}</div>
  },
  {
    title: 'Phone',
    renderer: (cellText) => <div>{cellText.phone || ''}</div>
  },
  {
    title: 'Avg. Order',
    renderer: (cellText) => <div>{renderAmounts(cellText.spendPerOrder)}</div>
  }
];

export const FIELDS_CASHBACK_USAGE = [
  {
    title: 'Order Date',
    renderer: (cellText) => (
      <div className='cellNoWrap'>{renderDate(cellText.orderDate, '-')}</div>
    )
  },
  {
    title: 'First name',
    renderer: (cellText) => <div>{cellText.firstName || ''}</div>
  },
  {
    title: 'Last name',
    renderer: (cellText) => <div>{cellText.lastName || ''}</div>
  },
  {
    title: 'Email',
    renderer: (cellText) => <div>{cellText.email || ''}</div>
  },
  {
    title: 'Phone',
    renderer: (cellText) => <div>{cellText.phone || ''}</div>
  },
  {
    title: 'Subtotal',
    renderer: (cellText) => <div>{renderAmounts(cellText.subtotal)}</div>
  },
  {
    title: 'Earned',
    renderer: (cellText) => <div>{renderAmounts(cellText.earned)}</div>
  },
  {
    title: 'Redeemed',
    renderer: (cellText) => <div>{renderAmounts(cellText.redeemed)}</div>
  }
];

export const TOTAL_CASHBACK_USAGE = [];

export const FIELDS_SALES_BREAKDOWN_SUMMARY = [
  {
    title: 'Date',
    renderer: (cellText) => (
      <div className='cellNoWrap'>{renderDateOnly(cellText.date)}</div>
    )
  },
  {
    title: 'Orders',
    renderer: (cellText) => (
      <div className='cellNoWrap'>{renderCount(cellText.numberOfOrders)}</div>
    )
  },
  {
    title: 'Gross',
    renderer: (cellText) => <div>{renderAmounts(cellText.gross)}</div>
  },
  {
    title: 'Avg',
    renderer: (cellText) => <div>{renderAmounts(cellText.avg)}</div>
  },
  {
    title: 'Tips',
    renderer: (cellText) => <div>{renderAmounts(cellText.tips)}</div>
  },
  {
    title: 'Tax',
    renderer: (cellText) => <div>{renderAmounts(cellText.tax)}</div>
  },
  {
    title: 'Discount',
    renderer: (cellText) => (
      <div>
        {renderAmounts(cellText.discount, <>&nbsp;&nbsp;$0.00</>, true)}
      </div>
    )
  }
];

export const TOTAL_SALES_BREAKDOWN_SUMMARY = [
  {
    title: 'Date',
    renderer: () => <div>Total</div>
  },
  {
    title: 'Orders',
    renderer: (cellText) => (
      <div className='cellNoWrap'>{renderCount(cellText.numberOfOrders)}</div>
    )
  },
  {
    title: 'Gross',
    renderer: (cellText) => <div>{renderAmounts(cellText.gross, '$0.00')}</div>
  },
  {
    title: 'Avg',
    renderer: (cellText) => <div>{renderAmounts(cellText.avg, '$0.00')}</div>
  },
  {
    title: 'Tips',
    renderer: (cellText) => <div>{renderAmounts(cellText.tips, '$0.00')}</div>
  },
  {
    title: 'Tax',
    renderer: (cellText) => <div>{renderAmounts(cellText.tax, '$0.00')}</div>
  },
  {
    title: 'Discount',
    renderer: (cellText) => (
      <div>
        {renderAmounts(cellText.discount, <>&nbsp;&nbsp;$0.00</>, true)}
      </div>
    )
  }
];

export const FIELDS_FEEDBACK_SUMMARY = [
  {
    title: 'Date',
    titleStyles: {},
    renderer: (cellText) => (
      <div className='cellNoWrap'>{renderDate(cellText.createdDate, '-')}</div>
    )
  },
  {
    title: 'Time',
    renderer: (cellText) => (
      <div className='cellNoWrap'>
        {cellText.createdDate ? moment(cellText.createdDate).format('LT') : '-'}
      </div>
    )
  },
  {
    title: 'Order ID',
    renderer: (cellText) => <div>{cellText.orderId || '-'}</div>
  },
  {
    title: 'Order type',
    renderer: (cellText) => (
      <div>{feedbackTypes[cellText.orderDeliveryType] || '-'}</div>
    )
  },
  {
    title: 'Score',
    renderer: (cellText) => (
      <div>{`${cellText.overallExperience}/5` || '-'}</div>
    )
  },
  {
    title: 'Additional information',
    titleStyles: {},
    renderer: (cellText) => (
      <div className='cellWordBreak'>
        {cellText?.additionalInformation || '-'}
      </div>
    )
  },
  {
    title: 'Customer Name',
    renderer: (cellText) => <div>{cellText.customerName || '-'}</div>
  },
  {
    title: 'Customer phone',
    renderer: (cellText) => (
      <div>
        {cellText.customerPhone
          ? cellText.customerPhone.replace(/say2eat_-|say2eat_/, '')
          : '-'}
      </div>
    )
  },
  {
    title: 'Customer email',
    renderer: (cellText) => <div>{cellText.customerEmail || '-'}</div>
  },
  {
    title: 'Google review',
    renderer: (cellText) => (
      <div>{cellText.googleReviewClicked ? 'Yes' : 'No'}</div>
    )
  }
];

export const FIELDS_SALES_SUMMARY = [
  {
    title: 'Order Date',
    renderer: (cellText) => (
      <div className='cellNoWrap'>{renderDate(cellText.salesDate, '-')}</div>
    )
  },
  {
    title: 'Subtotal',
    renderer: (cellText) => <div>{renderAmounts(cellText.subtotal)}</div>
  },
  {
    title: 'Tax',
    renderer: (cellText) => <div>{renderAmounts(cellText.tax)}</div>
  },
  {
    title: 'Tip',
    renderer: (cellText) => <div>{renderAmounts(cellText.tip)}</div>
  },
  {
    title: 'Discount',
    renderer: (cellText) => (
      <div>
        {renderAmounts(cellText.discount, <>&nbsp;&nbsp;$0.00</>, true)}
      </div>
    )
  },
  {
    title: 'Gross',
    renderer: (cellText) => <div>{renderAmounts(cellText.gross)}</div>
  },
  {
    title: (
      <div
      // style={{textAlign: 'center'}}
      >
        <span>
          Delivery and
          <br />
          Processing Fees
        </span>
      </div>
    ),
    renderer: (cellText) => (
      <div
      // className='centeredText'
      >
        <span>
          {renderAmounts(
            cellText.transactionAndDeliveryFees,
            <>&nbsp;&nbsp;$0.00</>,
            true
          )}
        </span>
      </div>
    )
  },
  {
    title: 'Refunds',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.refunds, <>&nbsp;&nbsp;$0.00</>, true)}</div>
    )
  }
  // {
  //   title: 'Payout Date',
  //   renderer: (cellText) => {
  //     if (cellText.payouts && cellText.payouts.length) {
  //       return (
  //         <>
  //           {cellText.payouts.map((item) => {
  //             return (
  //               <div className='reportsPayoutStatusContainer'>
  //                 <div className='cellNoWrap'>
  //                   {renderDate(item.arrivalDate, 'Future transaction')}
  //                 </div>
  //               </div>
  //             );
  //           })}
  //           {outOfRangePayoutsText(cellText)}
  //         </>
  //       );
  //     }
  //     return <div>Future transaction</div>;
  //   }
  // },
  // {
  //   title: 'Payout Amount',
  //   renderer: (cellText) => {
  //     if (cellText.payouts && cellText.payouts.length) {
  //       return (
  //         cellText.payouts.map((item) => {
  //           return (
  //             <div className='reportsPayoutStatusContainer'>
  //               <div className='cellNoWrap'>
  //                 {renderAmounts(item.amount, '-')}
  //               </div>
  //               {item.status === 'failed' && (
  //                 <span className='reportsPayoutStatus'>
  //                     Transaction failed
  //                   </span>
  //               )}
  //             </div>
  //           );
  //         })
  //       );
  //     }
  //     return <div>-</div>;
  //   }
  // }
];

export const FIELDS_PAYOUT_SUMMARY = [
  {
    title: 'Payout Date',
    renderer: (cellText) => (
      <div className='cellNoWrap'>
        {renderDateUtc(cellText.arrivalDate, '-')}
      </div>
    )
  },
  {
    title: 'Payout Amount',
    renderer: (cellText) => (
      <div className='reportsPayoutStatusContainer'>
        <div>{renderAmounts(cellText.amount)}</div>
        {cellText.status === 'failed' && (
          <span className='reportsPayoutStatus'>Transaction failed</span>
        )}
      </div>
    )
  }
];

export const FIELDS_BOOK_A_DRIVER = [
  {
    title: 'Order Date',
    renderer: (cellText) => (
      <div className='cellNoWrap'>
        {renderDate(cellText.order.placedAt.local, '-')}
      </div>
    )
  },
  {
    title: 'Order Time',
    renderer: (cellText) => (
      <div className='cellNoWrap'>
        {cellText.order.placedAt.local
          ? moment(cellText.order.placedAt.local).format('LT')
          : '-'}
      </div>
    )
  },
  {
    title: 'Subtotal',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.order.subtotal, '-')}</div>
    )
  },
  {
    title: 'Courier Tip',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.order.courierTip, '-')}</div>
    )
  },
  {
    title: 'Delivery Fee',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.order.deliveryFee, '-')}</div>
    )
  },
  {
    title: 'Customer Name',
    renderer: (cellText) => (
      <div className='cellNoWrap'>{cellText.order.customerName}</div>
    )
  },
  {
    title: 'Customer Address',
    renderer: (cellText) => (
      <div className='cellNoWrap'>{cellText.order.customerEmail}</div>
    )
  }
];

export const FIELDS_DISCOUNT_SUMMARY = [
  {
    title: 'Order Date',
    renderer: (cellText) => (
      <div className='cellNoWrap'>{renderDate(cellText.orderDate, '-')}</div>
    )
  },
  {
    title: '# Orders',
    renderer: (cellText) => (
      <div className='cellNoWrap'>{cellText.orderCount ?? '-'}</div>
    )
  },
  {
    title: 'Subtotal',
    renderer: (cellText) => <div>{renderAmounts(cellText.subtotal)}</div>
  },
  {
    title: 'Discount',
    renderer: (cellText) => (
      <div>
        {renderAmounts(cellText.discount, <>&nbsp;&nbsp;$0.00</>, true)}
      </div>
    )
  },
  {
    title: 'Gross',
    renderer: (cellText) => <div>{renderAmounts(cellText.gross)}</div>
  }
];

export const FIELDS_BAD_SUMMARY = [
  {
    title: 'Order Date',
    renderer: (cellText) => (
      <div className='cellNoWrap'>{renderDate(cellText?.chargeDate, '-')}</div>
    )
  },
  {
    title: 'Delivery Count',
    renderer: (cellText) => (
      <div className='cellNoWrap'>{cellText?.deliveryCount}</div>
    )
  },
  {
    title: 'Delivery Fee',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText?.deliveryFee, '-')}</div>
    )
  },
  {
    title: 'Tips',
    renderer: (cellText) => <div>{renderAmounts(cellText?.tips, '-')}</div>
  }
];

export const FIELDS_BYOC = [
  {
    title: 'Order Date',
    renderer: (cellText) => (
      <div className='cellNoWrap'>
        {renderDate(cellText.order.placedAt.local, '-')}
      </div>
    )
  },
  {
    title: 'Orders',
    renderer: (cellText) => (
      <div>{cellText.order.orders ? cellText.order.orders : '-'}</div>
    )
  },
  {
    title: 'Subtotal',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.order.subtotal, '-')}</div>
    )
  },
  {
    title: 'Tax',
    renderer: (cellText) => <div>{renderAmounts(cellText.order.tax, '-')}</div>
  },
  {
    title: 'Gross',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.order.gross, '-')}</div>
    )
  },
  {
    title: 'Delivery fee',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.order.deliveryFee, '-')}</div>
    )
  }
];

const SELF_DELIVERY_ORDER_DATE_FIELD = {
  title: 'Order Date',
  renderer: (cellText) => (
    <div className='cellNoWrap'>{renderDateOnly(cellText.orderDate)}</div>
  )
};

const SELF_DELIVERY_CHARGE_AMOUNT_FIELD = {
  title: 'Charge Amount',
  renderer: (cellText) => <div>{renderAmounts(cellText.chargeAmount, '-')}</div>
};

const SELF_DELIVERY_TOTAL_CHARGE_AMOUNT_FIELD = {
  title: 'Charge Amount',
  renderer: (cellText) => (
    <div>{renderAmounts(cellText.totalChargeAmount, '$0.00')}</div>
  )
};

export const FIELDS_SELF_DELIVERY_SUMMARY = [
  SELF_DELIVERY_ORDER_DATE_FIELD,
  SELF_DELIVERY_CHARGE_AMOUNT_FIELD,
  {
    title: 'Delivery Fees & Tips Paid by Customer',
    renderer: (cellText) => (
      <div>
        {renderAmounts(cellText.deliveryFeesAndTipsPaidByCustomer, '-')}
      </div>
    )
  },
  {
    title: 'Delivery Fee Paid by Store',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.deliveryFeePaidByStore, '-')}</div>
    )
  },
  {
    title: 'Credit Reimbursement',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.creditReimbursement, '-', true)}</div>
    )
  },
  {
    title: (
      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
        <span>Commission Saved</span>
        <ButtonInfo className='Outline ButtonInfoDrop marginLeftXsPlus'>
          <div className='DropInfo DropInfoRight DropInfoRightMob'>
            This field is based on your provided Marketplace and Self Delivery
            data. If it is empty or seems incorrect, update your settings above.
          </div>
        </ButtonInfo>
      </div>
    ),
    renderer: (cellText) => {
      // Show "-" if commissionSaved is null, undefined, or 0
      if (!cellText.commissionSaved || cellText.commissionSaved === 0) {
        return <div>-</div>;
      }
      return <div>{renderAmounts(cellText.commissionSaved, '-', false)}</div>;
    }
  }
];

export const TOTAL_SELF_DELIVERY_SUMMARY = [
  {
    title: 'Order Date',
    renderer: () => <div>Total</div>
  },
  SELF_DELIVERY_TOTAL_CHARGE_AMOUNT_FIELD,
  {
    title: 'Delivery Fees & Tips Paid by Customer',
    renderer: (cellText) => (
      <div>
        {renderAmounts(
          cellText.totalDeliveryFeesAndTipsPaidByCustomer,
          '$0.00'
        )}
      </div>
    )
  },
  {
    title: 'Delivery Fee Paid by Store',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.totalDeliveryFeePaidByStore, '$0.00')}</div>
    )
  },
  {
    title: 'Credit Reimbursement',
    renderer: (cellText) => (
      <div>
        {renderAmounts(cellText.totalCreditReimbursement, '$0.00', false)}
      </div>
    )
  },
  {
    title: (
      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
        <span>Commission Saved</span>
        <ButtonInfo className='Outline ButtonInfoDrop marginLeftXsPlus'>
          <div className='DropInfo DropInfoRight DropInfoRightMob'>
            This field is based on your provided Marketplace and Self Delivery
            data. If it is empty or seems incorrect, update your settings above.
          </div>
        </ButtonInfo>
      </div>
    ),
    renderer: (cellText) => {
      // Show the total commission saved, or "-" if null/undefined, or "$0.00" if 0
      const value = cellText.totalCommissionSaved;
      if (value == null) {
        return <div>-</div>;
      }
      if (value === 0) {
        return <div>$0.00</div>;
      }
      return <div>{renderAmounts(value, '-', false)}</div>;
    }
  }
];

// Split variant of the Self Delivery Summary columns
// Gated by the dashboard_self_delivery_report_split_fee_tip location feature
export const FIELDS_SELF_DELIVERY_SUMMARY_SPLIT = [
  SELF_DELIVERY_ORDER_DATE_FIELD,
  SELF_DELIVERY_CHARGE_AMOUNT_FIELD,
  {
    title: 'Delivery Fee',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.deliveryFeeOnlyPaidByCustomer, '-')}</div>
    )
  },
  {
    title: 'Tip Paid by Customer',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.tipPaidByCustomer, '-')}</div>
    )
  },
  {
    title: 'Delivery Fee Paid by Store',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.deliveryFeePaidByStore, '-')}</div>
    )
  },
  {
    title: 'Credit Reimbursement',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.creditReimbursement, '-', true)}</div>
    )
  },
  {
    title: (
      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
        <span>Commission Saved</span>
        <ButtonInfo className='Outline ButtonInfoDrop marginLeftXsPlus'>
          <div className='DropInfo DropInfoRight DropInfoRightMob'>
            This field is based on your provided Marketplace and Self Delivery
            data. If it is empty or seems incorrect, update your settings above.
          </div>
        </ButtonInfo>
      </div>
    ),
    renderer: (cellText) => {
      if (!cellText.commissionSaved || cellText.commissionSaved === 0) {
        return <div>-</div>;
      }
      return <div>{renderAmounts(cellText.commissionSaved, '-', false)}</div>;
    }
  }
];

export const TOTAL_SELF_DELIVERY_SUMMARY_SPLIT = [
  {
    title: 'Order Date',
    renderer: () => <div>Total</div>
  },
  SELF_DELIVERY_TOTAL_CHARGE_AMOUNT_FIELD,
  {
    title: 'Delivery Fee',
    renderer: (cellText) => (
      <div>
        {renderAmounts(cellText.totalDeliveryFeeOnlyPaidByCustomer, '$0.00')}
      </div>
    )
  },
  {
    title: 'Tip Paid by Customer',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.totalTipPaidByCustomer, '$0.00')}</div>
    )
  },
  {
    title: 'Delivery Fee Paid by Store',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.totalDeliveryFeePaidByStore, '$0.00')}</div>
    )
  },
  {
    title: 'Credit Reimbursement',
    renderer: (cellText) => (
      <div>
        {renderAmounts(cellText.totalCreditReimbursement, '$0.00', false)}
      </div>
    )
  },
  {
    title: (
      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
        <span>Commission Saved</span>
        <ButtonInfo className='Outline ButtonInfoDrop marginLeftXsPlus'>
          <div className='DropInfo DropInfoRight DropInfoRightMob'>
            This field is based on your provided Marketplace and Self Delivery
            data. If it is empty or seems incorrect, update your settings above.
          </div>
        </ButtonInfo>
      </div>
    ),
    renderer: (cellText) => {
      const value = cellText.totalCommissionSaved;
      if (value == null) {
        return <div>-</div>;
      }
      if (value === 0) {
        return <div>$0.00</div>;
      }
      return <div>{renderAmounts(value, '-', false)}</div>;
    }
  }
];

// Charge-date view: Charge Date column first, Order Date second
const SELF_DELIVERY_CHARGE_DATE_FIELD = {
  title: 'Charge Date',
  renderer: (cellText) => (
    <div className='cellNoWrap'>{renderDateUtc(cellText.chargeDate, '-')}</div>
  )
};

const withChargeDateColumn = (fields) => [
  SELF_DELIVERY_CHARGE_DATE_FIELD,
  ...fields
];

// Total row keeps "Total" in the first cell and leaves Order Date blank
const withChargeDateTotalColumn = ([totalCell, ...rest]) => [
  {...totalCell, title: 'Charge Date'},
  {title: 'Order Date', renderer: () => <div />},
  ...rest
];

export const FIELDS_SELF_DELIVERY_SUMMARY_BY_CHARGE_DATE = withChargeDateColumn(
  FIELDS_SELF_DELIVERY_SUMMARY
);
export const TOTAL_SELF_DELIVERY_SUMMARY_BY_CHARGE_DATE = withChargeDateTotalColumn(
  TOTAL_SELF_DELIVERY_SUMMARY
);
export const FIELDS_SELF_DELIVERY_SUMMARY_SPLIT_BY_CHARGE_DATE = withChargeDateColumn(
  FIELDS_SELF_DELIVERY_SUMMARY_SPLIT
);
export const TOTAL_SELF_DELIVERY_SUMMARY_SPLIT_BY_CHARGE_DATE = withChargeDateTotalColumn(
  TOTAL_SELF_DELIVERY_SUMMARY_SPLIT
);

export const FIELDS_SELF_DELIVERY_INVOICES = [
  {
    title: 'Invoice Number',
    renderer: (row) => <div className='cellNoWrap'>{row.stripeNumber}</div>
  },
  {
    title: 'Charge Date',
    renderer: (row) => (
      <div className='cellNoWrap'>{renderDateUtc(row.chargeDate, '-')}</div>
    )
  },
  {
    title: 'Total',
    renderer: (row) => <div>{renderAmounts(row.total, '$0.00')}</div>
  },
  {
    title: 'Delivery Fees & Tips Paid by Customer',
    renderer: (row) => (
      <div>{renderAmounts(row.deliveryFeesAndTipsPaidByCustomer, '$0.00')}</div>
    )
  },
  {
    title: 'Delivery Fees Paid by Store',
    renderer: (row) => (
      <div>{renderAmounts(row.deliveryFeePaidByStore, '$0.00')}</div>
    )
  },
  {
    title: 'Credit Reimbursement',
    renderer: (row) => (
      <div>{renderAmounts(row.creditReimbursement, '$0.00')}</div>
    )
  },
  {
    title: (
      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
        <span>Commission Saved</span>
        <ButtonInfo className='Outline ButtonInfoDrop marginLeftXsPlus'>
          <div className='DropInfo DropInfoRight DropInfoRightMob'>
            This field is based on your provided Marketplace and Self Delivery
            data. If it is empty or seems incorrect, update your settings above.
          </div>
        </ButtonInfo>
      </div>
    ),
    renderer: (row) =>
      row.commissionSaved ? (
        <div>{renderAmounts(row.commissionSaved, '-')}</div>
      ) : (
        <div>-</div>
      )
  },
  {
    title: <div style={{textAlign: 'right'}}>Documents</div>,
    renderer: (row) => <SelfDeliveryInvoiceActions row={row} />
  }
];

export const FIELDS_REFUNDS_SUMMARY = [
  {
    title: 'Order Date',
    renderer: (cellText) => (
      <div className='cellNoWrap'>{renderDate(cellText.orderDate, '-')}</div>
    )
  },
  {
    title: 'Refund Request Date',
    renderer: (cellText) => (
      <div className='cellNoWrap'>{renderDate(cellText.requestDate, '-')}</div>
    )
  },
  {
    title: 'Order Type',
    renderer: (cellText) => (
      <div>{ORDER_TYPES[OrderType[cellText.orderDeliveryType]] || '-'}</div>
    )
  },
  {
    title: (
      <div
      // style={{textAlign: 'center'}}
      >
        <span>Refund Amount</span>
      </div>
    ),
    renderer: (cellText) => (
      <div
      // className='centeredText'
      >
        <span>
          {renderAmounts(cellText.amount, <>&nbsp;&nbsp;$0.00</>, true)}
        </span>
      </div>
    )
  },
  {
    title: 'Payment Date',
    renderer: (cellText) => (
      <div className='cellNoWrap'>
        {renderDate(cellText.withheldDate, 'Pending')}
      </div>
    )
  },
  {
    title: 'Customer Name',
    renderer: (cellText) => <div>{cellText.customerName || '-'}</div>
  },
  {
    title: 'Order ID',
    renderer: (cellText) => (
      <div>
        {cellText.orderInvoiceId
          ? cellText.orderInvoiceId.replace(/say2eat_-|say2eat_/, '')
          : '-'}
      </div>
    )
  }
];

export const TOTAL_SALES_SUMMARY = [
  {
    title: 'Order Date',
    renderer: () => <div>Total</div>
  },
  {
    title: 'Subtotal',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.subtotal, '$0.00')}</div>
    )
  },
  {
    title: 'Tax',
    renderer: (cellText) => <div>{renderAmounts(cellText.tax, '$0.00')}</div>
  },
  {
    title: 'Tip',
    renderer: (cellText) => <div>{renderAmounts(cellText.tip, '$0.00')}</div>
  },
  {
    title: 'Discount',
    renderer: (cellText) => (
      <div>
        {renderAmounts(cellText.discount, <>&nbsp;&nbsp;$0.00</>, true)}
      </div>
    )
  },
  {
    title: 'Gross',
    renderer: (cellText) => <div>{renderAmounts(cellText.gross, '$0.00')}</div>
  },
  {
    title: 'Delivery and Processing Fees',
    renderer: (cellText) => (
      <div
      // className='centeredText'
      >
        <span>
          {renderAmounts(
            cellText.transactionAndDeliveryFees,
            <>&nbsp;&nbsp;$0.00</>,
            true
          )}
        </span>
      </div>
    )
  },
  {
    title: 'Refunds',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.refunds, <>&nbsp;&nbsp;$0.00</>, true)}</div>
    )
  }
  // {
  //   title: 'Payout Date',
  //   renderer: () => <div>-</div>
  // },
  // {
  //   title: 'Payout Amount',
  //   renderer: (cellText) => (
  //     <div className='reportsPayoutStatusContainer'>
  //       <div>{renderAmounts(cellText.payoutAmount)}</div>
  //       {cellText.payoutStatus && (
  //         <span className='reportsPayoutStatus'>Transaction failed</span>
  //       )}
  //     </div>
  //   )
  // }
];

export const TOTAL_CUSTOMERS = [];

export const TOTAL_PAYOUT_SUMMARY = [
  {
    title: 'Payout Date',
    renderer: () => <div>Total</div>
  },
  {
    title: 'Payout Amount',
    renderer: (cellText) => (
      <div className='reportsPayoutStatusContainer'>
        <div>{renderAmounts(cellText.amount)}</div>
        {cellText.status && (
          <span className='reportsPayoutStatus'>Transaction failed</span>
        )}
      </div>
    )
  }
];

export const TOTAL_REFUNDS_SUMMARY = [
  {
    title: 'Order Date',
    renderer: () => <div>Total</div>
  },
  {
    renderer: () => ''
  },
  {
    renderer: () => ''
  },
  {
    title: 'Payout Amount',
    renderer: (cellText) => (
      <div
      // className='centeredText'
      >
        <span>
          {renderAmounts(cellText.totalAmount, <>&nbsp;&nbsp;$0.00</>, true)}
        </span>
      </div>
    )
  },
  {
    renderer: () => ''
  },
  {
    renderer: () => ''
  },
  {
    renderer: () => ''
  }
];

export const TOTAL_DISCOUNT_SUMMARY = [
  {
    title: 'Order Date',
    renderer: () => <div>Total</div>
  },
  {
    title: '# Orders',
    renderer: (cellText) => (
      <div className='cellNoWrap'>{cellText.orderCount ?? '-'}</div>
    )
  },
  {
    title: 'Subtotal',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText.subtotal, '$0.00')}</div>
    )
  },
  {
    title: 'Discount',
    renderer: (cellText) => (
      <div>
        {renderAmounts(cellText.discount, <>&nbsp;&nbsp;$0.00</>, true)}
      </div>
    )
  },
  {
    title: 'Gross',
    renderer: (cellText) => <div>{renderAmounts(cellText.gross, '$0.00')}</div>
  }
];

export const TOTAL_BAD_SUMMARY = [
  {
    title: 'Order Date',
    renderer: () => <div>Total</div>
  },
  {
    title: 'Delivery Count',
    renderer: (cellText) => (
      <div className='cellNoWrap'>{cellText?.deliveryCount}</div>
    )
  },
  {
    title: 'Delivery Fee',
    renderer: (cellText) => (
      <div>{renderAmounts(cellText?.deliveryFee, '-')}</div>
    )
  },
  {
    title: 'Tips',
    renderer: (cellText) => <div>{renderAmounts(cellText?.tips, '-')}</div>
  }
];

export const TOTAL_FEEDBACK_SUMMARY = [
  {
    title: 'Order Date',
    renderer: () => <div>Total</div>
  },
  {
    renderer: () => ''
  },
  {
    renderer: () => ''
  },
  {
    title: 'Payout Amount',
    renderer: (cellText) => (
      <div
      // className='centeredText'
      >
        <span>
          {renderAmounts(cellText.totalAmount, <>&nbsp;&nbsp;$0.00</>, true)}
        </span>
      </div>
    )
  },
  {
    renderer: () => ''
  },
  {
    renderer: () => ''
  },
  {
    renderer: () => ''
  }
];

export {
  SELF_DELIVERY_INVOICES_PERIODS,
  SELF_DELIVERY_INVOICES_PERIOD_OPTIONS,
  SELF_DELIVERY_INVOICES_PERIOD_TITLES,
  getSelfDeliveryInvoicesDateRange
} from 'helpers/constants/reports/selfDeliveryInvoices';
